/**
 * The app language — English, Deutsch or 中文 — one choice for the UI text,
 * the language agents reply in, and the tray and menus. What dictation listens
 * for is its own setting: someone who reads the app in Chinese may still
 * dictate in English.
 *
 * The app is in English, onboarding included, until the user picks another
 * language in Settings. The choice is stored locally and mirrored across
 * windows; the local value is the source of truth and is pushed to the account
 * on sign-in and on every change.
 */
import { invoke } from "@tauri-apps/api/core";
import { create } from "zustand";

import { updateAppLanguage } from "@/api/auth";
import { activateLanguage, APP_LANGUAGES, type AppLanguage } from "@/i18n";
import { isTauri } from "@/lib/is-tauri";
import { mirroredStore } from "@/lib/mirrored-store";
import {
  spokenLanguagesOrDefault,
  useDictationSettingsStore,
} from "./dictation-settings-store";

const STORAGE_KEY = "denker.app-language";

export type AppLanguageState = {
  /** The language the user picked, or null for English. */
  choice: AppLanguage | null;
};

type Actions = {
  setLanguage: (language: AppLanguage) => void;
};

export function parseAppLanguage(value: unknown): AppLanguage | null {
  return APP_LANGUAGES.includes(value as AppLanguage)
    ? (value as AppLanguage)
    : null;
}

function loadChoice(): AppLanguage | null {
  try {
    return parseAppLanguage(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistChoice(choice: AppLanguage | null) {
  try {
    if (choice) window.localStorage.setItem(STORAGE_KEY, choice);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the choice still applies for this session.
  }
}

export const useAppLanguageStore = create<AppLanguageState & Actions>()(
  mirroredStore("appLanguage", (set, get) => ({
    choice: typeof window === "undefined" ? null : loadChoice(),
    setLanguage: (language) => {
      keepSpokenLanguages(effectiveAppLanguage(get().choice), language);
      set({ choice: language });
      applyToNativeMenus(language);
      pushToAccount(language);
    },
  })),
);

/** "Languages I speak" defaults to the app language and English. A switch,
 *  picked here or adopted from the account, saves the list the user sees
 *  first, so it stays theirs rather than following the app into a language
 *  they may not speak. */
function keepSpokenLanguages(from: AppLanguage, to: AppLanguage) {
  const dictation = useDictationSettingsStore.getState();
  if (from === to || dictation.spokenLanguages) return;
  dictation.setSpokenLanguages(spokenLanguagesOrDefault(null, from));
}

let pushes: Promise<unknown> = Promise.resolve();

/** Tells the account, one request after another so quick switches land in
 *  order. Signed out or offline: the next sign-in pushes it again. */
function pushToAccount(language: AppLanguage) {
  pushes = pushes
    .then(() => updateAppLanguage(language))
    .catch(() => undefined);
}

/** Renders `language`; a catalogue that fails to load keeps the UI usable in
 *  English rather than leaving it blank or half-switched. */
async function render(language: AppLanguage): Promise<void> {
  try {
    await activateLanguage(language);
  } catch (error) {
    console.warn("[app-language] could not load", language, error);
    await activateLanguage("en");
  }
}

/** The language the app is in: the user's choice, else English. */
export function effectiveAppLanguage(
  choice: AppLanguage | null = useAppLanguageStore.getState().choice,
): AppLanguage {
  return choice ?? "en";
}

/** Reactive `effectiveAppLanguage`. */
export function useEffectiveAppLanguage(): AppLanguage {
  return effectiveAppLanguage(useAppLanguageStore((state) => state.choice));
}

/** The native tray and menus follow the language. Idempotent, so every
 *  window may call this at start-up without coordinating. */
function applyToNativeMenus(language: AppLanguage) {
  if (isTauri())
    void invoke("set_app_language", { language }).catch(() => undefined);
}

/**
 * Renders the app language. Awaited before React mounts, so nothing ever
 * starts in the wrong language.
 */
export async function initAppLanguage(): Promise<void> {
  const language = effectiveAppLanguage();
  await render(language);
  applyToNativeMenus(language);
}

/**
 * Reconciles this device with the account once the signed-in user is known.
 * A language picked here wins and is pushed. Without a pick, a language the
 * account already has (picked on another device) is adopted.
 */
export function syncAppLanguageToAccount(
  accountLanguage: AppLanguage | null | undefined,
) {
  const { choice } = useAppLanguageStore.getState();
  if (choice) {
    if (accountLanguage !== choice) pushToAccount(choice);
    return;
  }
  if (accountLanguage) {
    keepSpokenLanguages(effectiveAppLanguage(choice), accountLanguage);
    useAppLanguageStore.setState({ choice: accountLanguage });
    applyToNativeMenus(accountLanguage);
  }
}

// Every window renders the language it is told about, including a choice
// mirrored in from another window.
useAppLanguageStore.subscribe((state, previous) => {
  if (state.choice === previous.choice) return;
  persistChoice(state.choice);
  void render(effectiveAppLanguage(state.choice));
});
